import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getSalesReturns, createSalesReturn, getAccounts, getOrders, generateReturnNumber } from '@/services/firestore'
import { useAuth } from '@/contexts/AuthContext'
import { RotateCcw, Plus, X, Save, RefreshCw } from 'lucide-react'

export const Route = createFileRoute('/admin/sales-returns')({ component: SalesReturnsPage })

function SalesReturnsPage() {
    const { user } = useAuth()
    const [returns, setReturns] = useState<any[]>([])
    const [orders, setOrders] = useState<any[]>([])
    const [accounts, setAccounts] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [saving, setSaving] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState<any>(null)
    const [returnItems, setReturnItems] = useState<any[]>([])
    const [reason, setReason] = useState('')
    const [refundMethod, setRefundMethod] = useState<'cash' | 'credit_note'>('cash')

    useEffect(() => { load() }, [])
    const load = async () => {
        setLoading(true)
        const [r, o, a] = await Promise.all([getSalesReturns(), getOrders({ status: 'completed' }), getAccounts()])
        setReturns(r); setOrders(o); setAccounts(a)
        setLoading(false)
    }

    const selectOrder = (orderId: string) => {
        const order = orders.find(o => o.id === orderId)
        if (!order) return
        setSelectedOrder(order)
        setReturnItems((order.items || []).map((item: any) => ({ ...item, returnQty: 0, selected: false })))
    }

    const toggleItem = (idx: number) => {
        setReturnItems(prev => prev.map((item, i) => i === idx ? { ...item, selected: !item.selected, returnQty: !item.selected ? item.quantity : 0 } : item))
    }

    const handleCreate = async () => {
        setSaving(true)
        try {
            const items = returnItems.filter(i => i.selected && i.returnQty > 0).map(i => ({
                productId: i.productId, productName: i.name, quantity: i.returnQty, unitPrice: i.unitPrice, totalPrice: i.returnQty * i.unitPrice,
            }))
            const totalAmount = items.reduce((s, i) => s + i.totalPrice, 0)
            const salesReturnsAcc = accounts.find(a => a.code === '4002')
            const cashAcc = accounts.find(a => a.code === '1001')
            const receivableAcc = accounts.find(a => a.code === '1003')

            await createSalesReturn({
                returnNumber: generateReturnNumber('SR'), date: new Date().toISOString().split('T')[0],
                originalOrderId: selectedOrder.id, originalOrderNumber: selectedOrder.orderNumber,
                customerName: selectedOrder.customerName || 'Walk-in', items, totalAmount, reason, refundMethod,
                createdBy: user?.email || 'admin',
            }, { salesReturnsAccId: salesReturnsAcc?.id || '', cashAccId: cashAcc?.id || '', receivableAccId: receivableAcc?.id || '' })
            await load()
            setShowModal(false); setSelectedOrder(null); setReturnItems([]); setReason('')
        } finally { setSaving(false) }
    }

    const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2 })

    return (
        <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><RotateCcw className="w-6 h-6 text-blue-500" />Sales Returns</h1>
                    <p className="text-xs text-slate-500 mt-0.5">Process customer returns and refunds</p>
                </div>
                <button onClick={() => { setShowModal(true); setSelectedOrder(null); setReturnItems([]) }}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
                    <Plus className="w-4 h-4" />New Return
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : returns.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                    <RotateCcw className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-semibold">No sales returns yet</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b border-slate-100">
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Return #</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Original Invoice</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Customer</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Amount</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Refund</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Reason</th>
                        </tr></thead>
                        <tbody>
                            {returns.map((r, i) => (
                                <tr key={r.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${i % 2 ? 'bg-slate-25' : ''}`}>
                                    <td className="px-4 py-3 font-mono text-xs font-bold text-slate-500">{r.returnNumber}</td>
                                    <td className="px-4 py-3 text-slate-600">{r.date}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-blue-600">{r.originalOrderNumber}</td>
                                    <td className="px-4 py-3 font-semibold text-slate-700">{r.customerName}</td>
                                    <td className="px-4 py-3 text-right font-mono font-bold text-red-600">{f(r.totalAmount)}</td>
                                    <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.refundMethod === 'cash' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>{r.refundMethod === 'cash' ? 'Cash' : 'Credit Note'}</span></td>
                                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[150px] truncate">{r.reason}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-slate-800">New Sales Return</h2>
                            <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Select Original Invoice</label>
                                <select onChange={e => selectOrder(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50">
                                    <option value="">Choose invoice...</option>
                                    {orders.map(o => <option key={o.id} value={o.id}>{o.orderNumber} — {o.customerName || 'Walk-in'} — PKR {f(o.totalAmount)}</option>)}
                                </select>
                            </div>

                            {selectedOrder && returnItems.length > 0 && (
                                <>
                                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead><tr className="bg-slate-50">
                                                <th className="px-3 py-2 text-left">Select</th><th className="px-3 py-2 text-left">Product</th>
                                                <th className="px-3 py-2 text-right">Sold Qty</th><th className="px-3 py-2 text-right">Return Qty</th>
                                                <th className="px-3 py-2 text-right">Unit Price</th><th className="px-3 py-2 text-right">Total</th>
                                            </tr></thead>
                                            <tbody>
                                                {returnItems.map((item, idx) => (
                                                    <tr key={idx} className={`border-t border-slate-100 ${item.selected ? 'bg-blue-50' : ''}`}>
                                                        <td className="px-3 py-2"><input type="checkbox" checked={item.selected} onChange={() => toggleItem(idx)} /></td>
                                                        <td className="px-3 py-2 font-semibold">{item.name}</td>
                                                        <td className="px-3 py-2 text-right">{item.quantity}</td>
                                                        <td className="px-3 py-2 text-right">
                                                            {item.selected && <input type="number" min={1} max={item.quantity} value={item.returnQty}
                                                                onChange={e => setReturnItems(prev => prev.map((it, i) => i === idx ? { ...it, returnQty: Math.min(parseInt(e.target.value) || 0, item.quantity) } : it))}
                                                                className="w-16 px-2 py-1 border rounded text-right" />}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-mono">{f(item.unitPrice)}</td>
                                                        <td className="px-3 py-2 text-right font-mono font-bold">{item.selected ? f(item.returnQty * item.unitPrice) : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-bold text-slate-600 uppercase">Refund Method</label>
                                            <select value={refundMethod} onChange={e => setRefundMethod(e.target.value as any)} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50">
                                                <option value="cash">Cash Refund</option>
                                                <option value="credit_note">Credit Note</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-slate-600 uppercase">Reason</label>
                                            <input value={reason} onChange={e => setReason(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" placeholder="Reason for return" />
                                        </div>
                                    </div>

                                    <div className="text-right text-lg font-bold text-red-600">
                                        Total Refund: PKR {f(returnItems.filter(i => i.selected).reduce((s: number, i: any) => s + (i.returnQty * i.unitPrice), 0))}
                                    </div>

                                    <button onClick={handleCreate} disabled={saving || !returnItems.some(i => i.selected && i.returnQty > 0)}
                                        className="w-full flex items-center justify-center gap-2 bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">
                                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{saving ? 'Processing...' : 'Process Return'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
